import React from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn } from '../adminlayout/designStandard';

const inputStyle = { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg)', color: 'var(--text)', width: '100%', maxWidth: '100%', minWidth: 0, minHeight: 40, boxSizing: 'border-box', fontSize: 12, lineHeight: 1.25 };
const readOnlyInputStyle = { ...inputStyle, background: 'rgba(148,163,184,0.08)', fontWeight: 800, textAlign: 'right' };
const labelStyle = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 };

function numberValue(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function parseNumber(value) {
  const text = String(value || '').replace(/,/g, '').split('.')[0];
  const sign = text.trim().startsWith('-') ? -1 : 1;
  const digits = text.replace(/[^\d]/g, '');
  if (digits === '' && sign < 0) return '-';
  return digits === '' ? null : sign * Number(digits);
}

export function Field({ label, value, onChange, type = 'text', options, textarea, required, readOnly }) {
  const isFormattedNumber = type === 'amount' || type === 'integer' || type === 'number';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={labelStyle}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</span>
      {options ? (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} style={inputStyle}>
          <option value="">선택</option>
          {options.map((option) => <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? option}</option>)}
        </select>
      ) : textarea ? (
        <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      ) : (
        <input
          type={isFormattedNumber ? 'text' : type}
          inputMode={isFormattedNumber ? 'text' : undefined}
          pattern={isFormattedNumber ? '-?[0-9,]*' : undefined}
          value={isFormattedNumber ? numberValue(value) : (value ?? '')}
          readOnly={readOnly}
          onFocus={(e) => {
            if (isFormattedNumber) e.target.select();
          }}
          onChange={(e) => {
            if (readOnly) return;
            const nextValue = e.target.value;
            if (isFormattedNumber) onChange(parseNumber(nextValue));
            else onChange(nextValue || null);
          }}
          style={readOnly ? readOnlyInputStyle : inputStyle}
        />
      )}
    </div>
  );
}

export function Modal({ title, icon = '📋', onClose, onSave, saving, children, maxWidth = 720, bodyStyle }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  return (
    <div style={overlay} {...overlayCloseProps}>
      <div style={{ ...modal, maxWidth }} onClick={(e) => e.stopPropagation()}>
        <div style={stdModalHeaderBar(8)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>{icon}</div>
            <div style={stdModalHeaderTitle}>{title}</div>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>✕</button>
        </div>
        <div style={{ ...body, ...(bodyStyle || {}) }}>{children}</div>
        <div style={footer}>
          <button onClick={onClose} style={cancelBtn}>취소</button>
          <button onClick={onSave} disabled={saving} style={saveBtn}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

export const Grid = ({ children, min = 180 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`, gap: 8, minWidth: 0 }}>{children}</div>
);

export const YN_OPTS = [{ value: 'true', label: '예' }, { value: 'false', label: '아니오' }];
export const PAX_OPTS = [{ value: 'adult', label: '성인' }, { value: 'child', label: '소아' }, { value: 'infant', label: '유아' }];

export const fromYN = (value) => (value === 'true' ? true : value === 'false' ? false : null);
export const toYN = (value) => (value === true ? 'true' : value === false ? 'false' : '');

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' };
const modal = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, width: 'min(100%, calc(100vw - 24px))', maxHeight: 'min(90vh, calc(100dvh - 24px))', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' };
const body = { padding: 16, overflowY: 'auto', display: 'grid', gap: 12 };
const footer = { padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 };
const cancelBtn = { padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' };
const saveBtn = { padding: '6px 14px', background: 'var(--accent-700, #7c3aed)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
